
import React, { useEffect, useState } from "react";
import { Modal, Pagination } from "react-bootstrap";
import "../styles/admindocument.css";

// Mapa de tipos de backend a frontend (para URLs y display)
const tipoMap = {
  "Selfie": "selfie",
  "DNI/Frente": "dniFront",
  "DNI/Dorso": "dniBack",
  "Carnet/Frente": "carnetFront",
  "Carnet/Dorso": "carnetBack"
};

// Normalizar API_URL (remover barra final si existe)
const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4004').replace(/\/$/, '');
const LEGAJOS_POR_PAGINA = 5;

// Helper para obtener headers de auth con JWT
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    console.warn('⚠️ No hay token de autenticación');
    return {};
  }
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

function AdminDocuments() {
  const [docs, setDocs] = useState({});
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageBlobs, setImageBlobs] = useState({}); // Cache de blob URLs
  const [lastUpdate, setLastUpdate] = useState(null); // Última actualización

  // Función para cargar imagen con autenticación y convertirla a blob URL
  const loadImageAsBlob = async (url, imageId) => {
    // Si ya está en cache, no recargar
    if (imageBlobs[imageId]) {
      return imageBlobs[imageId];
    }

    try {
      const response = await fetch(url, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      // Guardar en cache
      setImageBlobs(prev => ({ ...prev, [imageId]: blobUrl }));
      
      return blobUrl;
    } catch (err) {
      console.error(`Error cargando imagen ${imageId}:`, err);
      return null;
    }
  };

  const loadDocuments = () => {
    setLoading(true);
    setImageBlobs({}); // Limpiar cache de imágenes
    fetch(`${API_URL}/api/fotos/documents?_=${Date.now()}`, { // Cache-busting
      headers: getAuthHeaders(),
      cache: 'no-cache' // Forzar fetch fresco
    })
      .then(res => {
        if (!res.ok) throw new Error("Error al cargar documentos");
        return res.json();
      })
      .then(data => {
        console.log('📦 Documentos cargados:', Object.keys(data).length, 'legajos');
        console.log('🔍 Legajos disponibles:', Object.keys(data).sort());
        if (data['7393']) {
          console.log('✅ Legajo 7393 encontrado con', data['7393'].length, 'archivos:', data['7393']);
        } else {
          console.warn('⚠️ Legajo 7393 NO encontrado en la respuesta');
        }
        // Prefixar URLs relativas con API_URL para que apunten al backend correcto
        const docsWithFullUrls = {};
        Object.entries(data).forEach(([legajo, files]) => {
          docsWithFullUrls[legajo] = files.map(file => ({
            ...file,
            url: file.url.startsWith('http') ? file.url : `${API_URL}${file.url}`,
            imageId: `${legajo}_${file.tipo}_${file.name}` // ID único para cache
          }));
        });
        setDocs(docsWithFullUrls);
        setLastUpdate(new Date());
        setLoading(false);
      })
      .catch(err => {
        console.error("Error cargando documentos:", err);
        setError("No se pudieron cargar los documentos");
        setLoading(false);
      });
  };

  useEffect(() => {
    loadDocuments();
  }, []);

  // Limpiar blob URLs al desmontar para evitar memory leaks
  useEffect(() => {
    return () => {
      Object.values(imageBlobs).forEach(blobUrl => {
        URL.revokeObjectURL(blobUrl);
      });
    };
  }, [imageBlobs]);

  const handleDeleteOne = async (legajo, tipo, filename) => {
    if (!window.confirm(`¿Eliminar ${filename}?`)) return;
    
    setDeleting(true);
    const backendTipo = tipoMap[tipo] || tipo;
    
    try {
      const res = await fetch(`${API_URL}/api/fotos/delete/${legajo}/${backendTipo}/${filename}`, {
        method: "DELETE",
        headers: getAuthHeaders()
      });
      
      const data = await res.json();
      
      if (data.success) {
        loadDocuments();
        alert("✅ Imagen eliminada correctamente");
      } else {
        alert(`❌ Error al eliminar imagen: ${data.error || 'Error desconocido'}`);
      }
    } catch (err) {
      console.error("❌ Error al eliminar imagen:", err);
      alert("❌ Error de conexión al eliminar");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteAll = async (legajo) => {
    if (!window.confirm(`¿Eliminar TODAS las imágenes del legajo ${legajo}?`)) return;
    
    setDeleting(true);
    
    try {
      const res = await fetch(`${API_URL}/api/fotos/delete-all/${legajo}`, { 
        method: "DELETE",
        headers: getAuthHeaders()
      });
      
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Error ${res.status}: ${text}`);
      }
      
      const data = await res.json();
      
      if (data.success) {
        loadDocuments();
        alert("✅ Carpeta eliminada correctamente");
      } else {
        alert("❌ Error al eliminar carpeta");
      }
    } catch (err) {
      console.error("❌ Error al eliminar carpeta:", err);
      alert("❌ Error de conexión al eliminar");
    } finally {
      setDeleting(false);
    }
  };

  const openImageAsPDF = (url, filename) => {
    // Abrir la imagen en una nueva pestaña
    window.open(url, '_blank');
  };

  const filteredLegajos = Object.entries(docs).filter(([legajo]) =>
    legajo.toLowerCase().includes(search.toLowerCase())
  );

  // Paginación
  const totalPages = Math.ceil(filteredLegajos.length / LEGAJOS_POR_PAGINA);
  const startIndex = (currentPage - 1) * LEGAJOS_POR_PAGINA;
  const endIndex = startIndex + LEGAJOS_POR_PAGINA;
  const currentLegajos = filteredLegajos.slice(startIndex, endIndex);

  // Cargar imágenes como blobs cuando cambian los documentos o la página
  useEffect(() => {
    if (currentLegajos.length > 0) {
      currentLegajos.forEach(([legajo, files]) => {
        files.forEach(file => {
          loadImageAsBlob(file.url, file.imageId);
        });
      });
    }
  }, [docs, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  if (loading) return <div className="loading">⏳ Cargando documentos...</div>;
  if (error) return <div className="error">❌ Error: {error}</div>;

  return (
    <div className="admin-documents">
      <div className="header-section">
        <h2>📂 Gestión de Documentos</h2>
        <p className="subtitle">Total: {filteredLegajos.length} legajos</p>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="🔎 Buscar por legajo..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button className="btn-secondary" onClick={() => setSearch("")}>Limpiar</button>
        <button className="btn-primary" onClick={loadDocuments} disabled={loading}>
          {loading ? '⏳ Cargando...' : '🔄 Recargar'}
        </button>
      </div>

      {lastUpdate && (
        <div className="alert alert-info mb-3" style={{ fontSize: '0.9em', padding: '0.5rem 1rem' }}>
          ℹ️ Última actualización: {lastUpdate.toLocaleTimeString('es-AR')} 
          {' '}- Si se subieron imágenes recientemente, haz clic en <strong>Recargar</strong>
        </div>
      )}

      {deleting && (
        <div className="alert alert-info">⏳ Eliminando archivo...</div>
      )}

      <div className="documents-container">
        {currentLegajos.map(([legajo, files]) => (
          <div className="legajo-card" key={legajo}>
            <div className="legajo-header">
              <div className="legajo-title">
                <h3>📁 Legajo {legajo}</h3>
                <span className="badge">{files.length} {files.length === 1 ? 'archivo' : 'archivos'}</span>
              </div>
              <button 
                className="btn-delete-all"
                onClick={() => handleDeleteAll(legajo)}
                disabled={deleting}
                title="Eliminar todas las imágenes"
              >
                🗑️ Eliminar todo
              </button>
            </div>

            <div className="images-grid">
              {files.map((f, i) => {
                const blobUrl = imageBlobs[f.imageId];
                return (
                  <div className="image-card" key={`${legajo}-${i}`}>
                    <div className="image-thumbnail" onClick={() => setSelectedImage(f)}>
                      {blobUrl ? (
                        <img src={blobUrl} alt={f.name} />
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '150px', background: '#f0f0f0' }}>
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Cargando...</span>
                          </div>
                        </div>
                      )}
                      <div className="image-overlay">
                        <span>👁️ Ver</span>
                      </div>
                    </div>
                    <div className="image-info">
                      <p className="image-type">{f.tipo}</p>
                      <div className="image-actions">
                        <button 
                          className="btn-view-pdf"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (blobUrl) {
                              window.open(blobUrl, '_blank');
                            }
                          }}
                          title="Abrir en nueva pestaña"
                          disabled={!blobUrl}
                        >
                          🖼️ Ver
                        </button>
                        <button 
                          className="btn-delete-one"
                          onClick={() => handleDeleteOne(legajo, f.tipo, f.name)}
                          disabled={deleting}
                          title="Eliminar imagen"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {filteredLegajos.length === 0 && (
        <div className="no-documents">
          <p>📭 No hay documentos para mostrar</p>
        </div>
      )}

      {totalPages > 1 && (
        <div className="pagination-container">
          <Pagination>
            <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
            <Pagination.Prev onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} />
            
            {[...Array(totalPages)].map((_, idx) => {
              const pageNum = idx + 1;
              if (
                pageNum === 1 ||
                pageNum === totalPages ||
                (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
              ) {
                return (
                  <Pagination.Item
                    key={pageNum}
                    active={pageNum === currentPage}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </Pagination.Item>
                );
              } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                return <Pagination.Ellipsis key={pageNum} disabled />;
              }
              return null;
            })}

            <Pagination.Next onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} />
            <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
          </Pagination>
        </div>
      )}

      {/* Modal de vista previa */}
      <Modal show={!!selectedImage} onHide={() => setSelectedImage(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{selectedImage?.tipo}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {selectedImage && (
            <>
              {imageBlobs[selectedImage.imageId] ? (
                <img src={imageBlobs[selectedImage.imageId]} alt={selectedImage.name} style={{ maxWidth: '100%', maxHeight: '70vh' }} />
              ) : (
                <div style={{ padding: '50px' }}>
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Cargando...</span>
                  </div>
                  <p className="mt-3">Cargando imagen...</p>
                </div>
              )}
              <p className="mt-3 text-muted">{selectedImage.name}</p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={() => setSelectedImage(null)}>Cerrar</button>
          <button 
            className="btn btn-info"
            onClick={() => {
              if (selectedImage && imageBlobs[selectedImage.imageId]) {
                window.open(imageBlobs[selectedImage.imageId], '_blank');
              }
            }}
            disabled={!selectedImage || !imageBlobs[selectedImage?.imageId]}
          >
            🖼️ Abrir en nueva pestaña
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => {
              if (selectedImage && imageBlobs[selectedImage.imageId]) {
                const link = document.createElement('a');
                link.href = imageBlobs[selectedImage.imageId];
                link.download = selectedImage.name;
                link.click();
              }
            }}
            disabled={!selectedImage || !imageBlobs[selectedImage?.imageId]}
          >
            ⬇️ Descargar
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default AdminDocuments;
